import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import {
  FaChartBar,
  FaProjectDiagram,
  FaEuroSign,
  FaEnvelope,
  FaFileAlt,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaInbox,
  FaPaperPlane,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
  FaShieldAlt,
} from 'react-icons/fa';
import DashboardHome from './DashboardHome';
import AdminProjects from './AdminProjects';
import AdminPricing from './AdminPricing';
import AdminMessages from './AdminMessages';
import AdminTexts from './AdminTexts';
import AdminSecurity from './AdminSecurity';
import { authFetch } from './authHelper';
import './Admin.css';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin');
  }, [navigate]);

  // Auto-expand messages submenu when on a messages route
  useEffect(() => {
    if (location.pathname.includes('/messages')) {
      setMessagesOpen(true);
    }
  }, [location.pathname]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(() => {
    authFetch('/api/admin/messages.php?folder=inbox')
      .then((res) => res.json())
      .then((data) => {
        if (data.unread_count !== undefined) {
          setUnreadCount(data.unread_count);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: <FaChartBar />, label: 'Statistiques' },
    { path: '/admin/dashboard/projets', icon: <FaProjectDiagram />, label: 'Projets' },
    { path: '/admin/dashboard/tarifs', icon: <FaEuroSign />, label: 'Tarifs' },
    { path: '/admin/dashboard/textes', icon: <FaFileAlt />, label: 'Textes du site' },
    { path: '/admin/dashboard/securite', icon: <FaShieldAlt />, label: 'Sécurité' },
  ];

  const messagesSubItems = [
    { path: '/admin/dashboard/messages/inbox', icon: <FaInbox />, label: 'Reçus' },
    { path: '/admin/dashboard/messages/sent', icon: <FaPaperPlane />, label: 'Envoyés' },
    { path: '/admin/dashboard/messages/trash', icon: <FaTrash />, label: 'Supprimés' },
  ];

  const isMessagesActive = location.pathname.includes('/messages');

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2><span>AGKG</span> <span className="gradient-text">Admin</span></h2>
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <FaTimes />
          </button>
        </div>
        <nav className="admin-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon} {item.label}
            </Link>
          ))}

          {/* Messages expandable menu */}
          <button
            className={`admin-nav-item admin-nav-expandable ${isMessagesActive ? 'active' : ''}`}
            onClick={() => setMessagesOpen(!messagesOpen)}
          >
            <FaEnvelope />
            <span>Messages</span>
            {unreadCount > 0 && (
              <span className="admin-nav-badge">{unreadCount}</span>
            )}
            <span className="admin-nav-chevron">
              {messagesOpen ? <FaChevronDown /> : <FaChevronRight />}
            </span>
          </button>

          {messagesOpen && (
            <div className="admin-nav-submenu">
              {messagesSubItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`admin-nav-item admin-nav-subitem ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  {item.icon} {item.label}
                  {item.path.includes('inbox') && unreadCount > 0 && (
                    <span className="admin-nav-badge">{unreadCount}</span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </nav>
        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            Voir le site
          </Link>
          <button className="admin-nav-item admin-logout" onClick={logout}>
            <FaSignOutAlt /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <button className="admin-menu-btn" onClick={() => setSidebarOpen(true)}>
            <FaBars />
          </button>
          <h3>Back-office AGKG Créations</h3>
        </header>
        <div className="admin-content">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="projets" element={<AdminProjects />} />
            <Route path="tarifs" element={<AdminPricing />} />
            <Route path="messages" element={<AdminMessages key="inbox" folder="inbox" onUnreadChange={setUnreadCount} />} />
            <Route path="messages/inbox" element={<AdminMessages key="inbox" folder="inbox" onUnreadChange={setUnreadCount} />} />
            <Route path="messages/sent" element={<AdminMessages key="sent" folder="sent" onUnreadChange={setUnreadCount} />} />
            <Route path="messages/trash" element={<AdminMessages key="trash" folder="trash" onUnreadChange={setUnreadCount} />} />
            <Route path="textes" element={<AdminTexts />} />
            <Route path="securite" element={<AdminSecurity />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
